/**
 * PayPal Payment Components
 * Checkout buttons and payment flow for String Sauce marketplace
 */

import { useState, useCallback, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import {
  CreditCard,
  Check,
  X,
  Loader2,
  ShoppingCart,
  DollarSign,
  AlertCircle,
} from 'lucide-react';
import { getBackendUrl } from '../../config/api';

const API_URL = getBackendUrl();

// PayPal configuration context
export const usePayPalConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API_URL}/api/paypal/config`);
        if (res.ok) {
          const data = await res.json();
          setConfig(data);
        }
      } catch (error) {
        console.error('Failed to fetch PayPal config:', error);
      }
      setLoading(false);
    };
    fetchConfig();
  }, []);

  return { config, loading };
};

/**
 * PayPal Checkout Button Component
 */
export const PayPalCheckoutButton = ({
  itemType,  // "song", "preset", "bundle"
  itemId,
  itemName,
  amount,
  sellerId,
  buyerId,
  onSuccess,
  onError,
  onCancel,
}) => {
  const { config, loading: configLoading } = usePayPalConfig();
  const [orderCreating, setOrderCreating] = useState(false);
  const [error, setError] = useState(null);

  // Create order on backend
  const createOrder = useCallback(async () => {
    setOrderCreating(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_URL}/api/paypal/orders/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          item_type: itemType,
          item_id: itemId,
          buyer_id: buyerId,
          amount: amount,
          seller_id: sellerId,
          currency: 'USD'
        })
      });
      
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to create order');
      }
      
      const data = await res.json();
      setOrderCreating(false);
      
      // If mock mode, simulate approval
      if (data.mock_mode) {
        return data.order_id;
      }
      
      return data.order_id;
    } catch (err) {
      setOrderCreating(false);
      setError(err.message);
      throw err;
    }
  }, [itemType, itemId, buyerId, amount, sellerId]);

  // Capture order after approval
  const onApprove = useCallback(async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/paypal/orders/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: data.orderID,
          buyer_id: buyerId
        })
      });
      
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to capture payment');
      }
      
      const captureData = await res.json();
      onSuccess?.(captureData);
    } catch (err) {
      setError(err.message);
      onError?.(err);
    }
  }, [buyerId, onSuccess, onError]);

  if (configLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
      </div>
    );
  }

  // Mock mode - simple button
  if (config?.mock_mode) {
    return (
      <div className="space-y-3">
        <Button
          onClick={async () => {
            try {
              const orderId = await createOrder();
              await onApprove({ orderID: orderId });
            } catch (err) {
              console.error('Mock purchase failed:', err);
            }
          }}
          disabled={orderCreating}
          className="w-full bg-[#0070ba] hover:bg-[#003087] text-white"
        >
          {orderCreating ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <CreditCard className="w-4 h-4 mr-2" />
          )}
          Pay ${amount.toFixed(2)} with PayPal
        </Button>
        <p className="text-[10px] text-amber-500 text-center">
          ⚠️ Test Mode - No real payment will be processed
        </p>
        {error && (
          <p className="text-xs text-red-400 text-center">{error}</p>
        )}
      </div>
    );
  }

  // Real PayPal buttons
  return (
    <PayPalScriptProvider options={{ 
      clientId: config?.client_id,
      currency: 'USD'
    }}>
      <div className="space-y-3">
        {error && (
          <div className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <p className="text-xs text-red-400">{error}</p>
          </div>
        )}
        
        <PayPalButtons
          style={{
            layout: 'vertical',
            color: 'blue',
            shape: 'rect',
            label: 'pay'
          }}
          createOrder={createOrder}
          onApprove={onApprove}
          onCancel={() => onCancel?.()}
          onError={(err) => {
            setError(err.message || 'Payment failed');
            onError?.(err);
          }}
        />
        
        <div className="text-center">
          <p className="text-[10px] text-zinc-500">
            Creator receives ${(amount * (config?.creator_share_percent || 70) / 100).toFixed(2)} ({config?.creator_share_percent || 70}%)
          </p>
        </div>
      </div>
    </PayPalScriptProvider>
  );
};

/**
 * Purchase Modal Component
 */
export const PurchaseModal = ({
  isOpen,
  onClose,
  item,
  itemType,
  buyerId,
  onPurchaseComplete,
}) => {
  const [status, setStatus] = useState('idle'); // idle, processing, success, error
  const [message, setMessage] = useState('');

  const handleSuccess = (data) => {
    setStatus('success');
    setMessage('Purchase complete! Item added to your library.');
    setTimeout(() => {
      onPurchaseComplete?.(data);
      onClose();
    }, 2000);
  };

  const handleError = (err) => {
    setStatus('error');
    setMessage(err.message || 'Purchase failed. Please try again.');
  };

  if (!isOpen || !item) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-violet-500/20 flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white">Complete Purchase</h3>
              <p className="text-xs text-zinc-500">Secure payment via PayPal</p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Item Details */}
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{item.icon || '🎵'}</span>
            <div className="flex-1">
              <p className="font-medium text-white">{item.name}</p>
              <p className="text-xs text-zinc-500 capitalize">{itemType}</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-white">${(item.price || 0).toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Status Messages */}
        <AnimatePresence mode="wait">
          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center py-8"
            >
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <p className="text-white font-medium">Payment Successful!</p>
              <p className="text-sm text-zinc-400 mt-1">{message}</p>
            </motion.div>
          )}

          {status === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center py-8"
            >
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                <X className="w-8 h-8 text-red-400" />
              </div>
              <p className="text-white font-medium">Payment Failed</p>
              <p className="text-sm text-red-400 mt-1">{message}</p>
              <Button
                onClick={() => setStatus('idle')}
                className="mt-4"
                variant="outline"
              >
                Try Again
              </Button>
            </motion.div>
          )}

          {status === 'idle' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <PayPalCheckoutButton
                itemType={itemType}
                itemId={item.id}
                itemName={item.name}
                amount={item.price || 0}
                sellerId={item.user_id || item.seller_id}
                buyerId={buyerId}
                onSuccess={handleSuccess}
                onError={handleError}
                onCancel={onClose}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer */}
        {status === 'idle' && (
          <div className="mt-6 pt-4 border-t border-zinc-800">
            <p className="text-[10px] text-zinc-600 text-center">
              By purchasing, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

/**
 * Seller Earnings Dashboard Component
 */
export const SellerEarnings = ({ userId }) => {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/paypal/earnings?user_id=${userId}`);
        if (res.ok) {
          const data = await res.json();
          setEarnings(data);
        }
      } catch (error) {
        console.error('Failed to fetch earnings:', error);
      }
      setLoading(false);
    };
    
    if (userId) fetchEarnings();
  }, [userId]);

  const requestPayout = async () => {
    if (!earnings?.pending_payout || earnings.pending_payout < 1) return;
    
    setRequesting(true);
    try {
      const res = await fetch(`${API_URL}/api/paypal/payouts/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          seller_id: userId,
          amount: earnings.pending_payout,
          currency: 'USD'
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        // Refresh earnings
        setEarnings(prev => ({
          ...prev,
          pending_payout: 0,
          total_paid_out: (prev?.total_paid_out || 0) + earnings.pending_payout
        }));
      }
    } catch (error) {
      console.error('Payout request failed:', error);
    }
    setRequesting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Earnings Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-zinc-900/50 border border-emerald-500/20 rounded-xl p-4 text-center">
          <DollarSign className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
          <p className="text-xl font-bold text-white">
            ${(earnings?.total_earnings || 0).toFixed(2)}
          </p>
          <p className="text-[10px] text-zinc-500">Total Earnings</p>
        </div>
        
        <div className="bg-zinc-900/50 border border-amber-500/20 rounded-xl p-4 text-center">
          <p className="text-xl font-bold text-amber-400">
            ${(earnings?.pending_payout || 0).toFixed(2)}
          </p>
          <p className="text-[10px] text-zinc-500">Available</p>
        </div>
        
        <div className="bg-zinc-900/50 border border-zinc-700 rounded-xl p-4 text-center">
          <p className="text-xl font-bold text-zinc-400">
            ${(earnings?.total_paid_out || 0).toFixed(2)}
          </p>
          <p className="text-[10px] text-zinc-500">Paid Out</p>
        </div>
      </div>

      {/* Payout Button */}
      {earnings?.pending_payout >= 1 && (
        <Button
          onClick={requestPayout}
          disabled={requesting}
          className="w-full bg-emerald-600 hover:bg-emerald-500"
        >
          {requesting ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <DollarSign className="w-4 h-4 mr-2" />
          )}
          Request Payout (${earnings.pending_payout.toFixed(2)})
        </Button>
      )}

      {/* Recent Transactions */}
      {earnings?.recent_transactions?.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-4">
          <h4 className="text-sm font-medium text-white mb-3">Recent Transactions</h4>
          <div className="space-y-2">
            {earnings.recent_transactions.map((txn, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                <span className="text-xs text-zinc-400">{txn.order_id?.slice(0, 12)}...</span>
                <span className="text-sm text-emerald-400">+${txn.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default {
  PayPalCheckoutButton,
  PurchaseModal,
  SellerEarnings,
  usePayPalConfig
};
